import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import {
  Permissions,
  usePermission,
  usePermissions,
} from '@billda/permissions-ui';
import { Features, useFeature } from '@billda/features-ui';
import { SideNav } from '@billda/design-system';
import { useShellStore } from '../../../stores/shellStore';
import { useNavigationHandler } from '../../../hooks/useNavigationHandler';
import { useCommandPalette } from '../../../../features/search/providers/CommandPaletteProvider';
import { useOnboardingStatus } from '../../../../features/onboarding/hooks/useOnboardingStatus';
import { buildSidebarSections } from './SidebarContent';
import { SidebarHeader } from './SidebarHeader';

/**
 * Portal sidebar on the DS SideNav organism — one component covers both
 * variants (docked column at lg+, overlay drawer below). Group collapse
 * keeps the shellStore pattern: only explicitly-collapsed keys are tracked,
 * keyed by group text exactly as before.
 */
export const ExpandedSidebar: React.FC = () => {
  const location = useLocation();
  const permissions = usePermissions();
  const handleNavigation = useNavigationHandler();
  const { openPalette } = useCommandPalette();

  const isDashboardEnabled = useFeature(Features.Dashboard.IsEnabled);
  const isWorkActivityEnabled = useFeature(Features.WorkActivity.IsEnabled);
  const isEnhancedFilesEnabled = useFeature(
    Features.Files.IsEnhancedFileManagerEnabled
  );
  const hasOrgManage = usePermission(Permissions.Organisation.Manage);
  const { data: onboardingStatus } = useOnboardingStatus(hasOrgManage);
  const gettingStartedCompleted = Boolean(
    onboardingStatus?.gettingStartedCompletedAt
  );

  const {
    sidebarOpen,
    closeSidebar,
    sidebarCollapsed,
    collapsedGroups,
    toggleGroupCollapsed,
  } = useShellStore(
    useShallow(state => ({
      sidebarOpen: state.sidebarOpen,
      closeSidebar: state.closeSidebar,
      sidebarCollapsed: state.sidebarCollapsed,
      collapsedGroups: state.collapsedGroups,
      toggleGroupCollapsed: state.toggleGroupCollapsed,
    }))
  );

  // Auto-close on route change — only the overlay reacts to sidebarOpen, so
  // this is a no-op while the docked desktop column is showing.
  useEffect(() => {
    if (sidebarOpen) {
      closeSidebar();
    }
    // Only react to location changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const sections = useMemo(() => {
    return buildSidebarSections({
      permissions,
      isActive: (path: string) => location.pathname === path,
      onNavigate: handleNavigation,
      isDashboardEnabled,
      isWorkActivityEnabled,
      isEnhancedFilesEnabled,
      gettingStartedCompleted,
    });
  }, [
    permissions,
    location.pathname,
    handleNavigation,
    isDashboardEnabled,
    isWorkActivityEnabled,
    isEnhancedFilesEnabled,
    gettingStartedCompleted,
  ]);

  return (
    <SideNav
      sections={sections}
      header={<SidebarHeader />}
      collapsedKeys={collapsedGroups}
      onToggleCollapsed={toggleGroupCollapsed}
      open={sidebarOpen}
      onClose={closeSidebar}
      collapsed={sidebarCollapsed}
      onSearch={openPalette}
      testId="nav-menu"
      searchTestId="expanded-nav-search"
    />
  );
};
